import { supabase, isDemoMode } from './supabase'

// WG Platforms shared brand tokens — see C:/simon/PUSH/BRAND.md
// (Shared Design Tokens v1). Primary = dark forest green #0d5a3b,
// hover #106b45, mint #80d0b0 (small text safe on white but not on
// mint-tinted grounds). Per-client brand overrides in brand_settings
// still take precedence when a client_group has its own brand row.
const DEFAULT_BRAND = {
  name: 'OrderiQ',
  logo_url: null,
  primary_color: '#0d5a3b',       // --accent
  primary_color_dark: '#106b45',   // --accent-2 (button hover, gradient stop)
  primary_color_light: '#80d0b0',  // --accent-soft (mint icons/highlights)
  text_color: '#ffffff',
  button_text_color: '#ffffff',
  sidebar_hover_color: '#ffffff',
  email_footer_html: '',
}

function hexToRgbTriplet(hex) {
  const h = hex.replace('#', '')
  const n = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h
  const r = parseInt(n.slice(0, 2), 16)
  const g = parseInt(n.slice(2, 4), 16)
  const b = parseInt(n.slice(4, 6), 16)
  return `${r} ${g} ${b}`
}

function mix(hex, withHex, amount) {
  // Crude linear mix between two hex colours, 0 = hex, 1 = withHex.
  const a = hex.replace('#', '').match(/.{2}/g).map(x => parseInt(x, 16))
  const b = withHex.replace('#', '').match(/.{2}/g).map(x => parseInt(x, 16))
  return a.map((v, i) => Math.round(v + (b[i] - v) * amount)).join(' ')
}

export function applyBrand(brand) {
  const b = { ...DEFAULT_BRAND, ...(brand || {}) }
  const root = document.documentElement
  root.style.setProperty('--brand', hexToRgbTriplet(b.primary_color))
  root.style.setProperty('--brand-dark', hexToRgbTriplet(b.primary_color_dark))
  root.style.setProperty('--brand-light', hexToRgbTriplet(b.primary_color_light))
  root.style.setProperty('--brand-50',  mix(b.primary_color, '#ffffff', 0.92))
  root.style.setProperty('--brand-100', mix(b.primary_color, '#ffffff', 0.85))
  root.style.setProperty('--brand-900', mix(b.primary_color_dark, '#000000', 0.6))
  // Sidebar text colour (over bg-brand-dark). Defaults to white.
  root.style.setProperty('--brand-text', b.text_color || '#ffffff')
  root.style.setProperty('--brand-text-rgb', hexToRgbTriplet(b.text_color || '#ffffff'))
  // Primary button text colour (over bg-brand). Falls back to sidebar text,
  // then to white — so brands that only set one colour still look coherent.
  root.style.setProperty('--brand-button-text', b.button_text_color || b.text_color || '#ffffff')
  // Sidebar nav item hover / active background colour (raw hex, applied at
  // low opacity). Defaults to white so dark-sidebar brands look identical.
  root.style.setProperty('--brand-sidebar-hover-rgb', hexToRgbTriplet(b.sidebar_hover_color || '#ffffff'))
}

export async function loadBrand(companyId = null) {
  if (isDemoMode) return DEFAULT_BRAND
  // Company-scoped first (via RPC, works for anon too).
  if (companyId) {
    const { data } = await supabase.rpc('get_active_brand', { p_company_id: companyId })
    if (data) return data
  }
  // Fall back to the global default.
  const { data } = await supabase
    .from('brand_settings')
    .select('*')
    .eq('scope', 'global')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle()
  return data || DEFAULT_BRAND
}

export { DEFAULT_BRAND }
